import { ImageGalleryProps } from "./types"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Image as RNImage } from "~/ui/primitives"
import styled, { useTheme } from "~/ui/styled"
import FastImage from "~/ui/FastImage"
import useMediaLibrary from "~hooks/useMediaLibrary"
import useNavigation from "~hooks/useNavigation"
import { Video } from "~components/video"
import { IconFont } from "~components/El"
import { useStores } from "~models"
import { getMediaPath, staticUrl, globalToast } from "~utils"
import { fitContainSize, getGalleryModalSize, getGalleryPreviewBounds } from "~utils/media-fit"
import { MessageTypes } from "~proto"
import { Loading } from "~widgets/loading"

const ModalShell = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.blockBackground};
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0 16px 48px rgba(0, 0, 0, 0.16),
    0 4px 16px rgba(0, 0, 0, 0.08);
`

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 72px;
`

const ToolbarGroupEnd = styled(ToolbarGroup)`
  justify-content: flex-end;
`

const ToolbarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`

const ToolbarMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 0;
  flex: 1;
`

const ToolbarTitle = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
  user-select: none;
`

const ToolbarSubtitle = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.secondaryText};
  user-select: none;
`

const PreviewArea = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  overflow: hidden;
`

const MediaFrame = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.03);
`

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.06);
  cursor: pointer;
  z-index: 2;
  transition: background 0.15s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0;
    pointer-events: none;
  }
`

const NavPrev = styled(NavButton)`
  left: 16px;
`

const NavNext = styled(NavButton)`
  right: 16px;
`

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, index = 0, onClose }) => {
  const theme = useTheme()
  const { messageStore } = useStores()
  const navigation = useNavigation()
  const { saveAssetAsync } = useMediaLibrary({ writeOnly: false })
  const [loading, setLoading] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(index)
  const [bounds, setBounds] = useState(getGalleryPreviewBounds)
  const [displaySize, setDisplaySize] = useState(() => {
    const b = getGalleryPreviewBounds()
    return { width: b.maxWidth, height: b.maxHeight }
  })
  const [modalSize, setModalSize] = useState(() => {
    const b = getGalleryPreviewBounds()
    return { width: b.maxModalWidth, height: b.maxModalHeight }
  })

  const handleClose = onClose ?? (() => navigation.goBack())
  const current = images[currentIndex] ?? images[0]
  const hasMultiple = images.length > 1
  const isVideo = current?.type === MessageTypes.VIDEO
  const iconColor = theme.colors.secondaryText

  useEffect(() => {
    setCurrentIndex(index)
  }, [index, images])

  useEffect(() => {
    const updateBounds = () => setBounds(getGalleryPreviewBounds())
    window.addEventListener("resize", updateBounds)
    return () => window.removeEventListener("resize", updateBounds)
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleClose()
        return
      }
      if (!hasMultiple) return
      if (event.key === "ArrowLeft") {
        setCurrentIndex((prev) => Math.max(0, prev - 1))
      }
      if (event.key === "ArrowRight") {
        setCurrentIndex((prev) => Math.min(images.length - 1, prev + 1))
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [handleClose, hasMultiple, images.length])

  const applyFit = useCallback(
    (naturalWidth: number, naturalHeight: number) => {
      const nextBounds = getGalleryPreviewBounds()
      const fitted = fitContainSize(naturalWidth, naturalHeight, nextBounds.maxWidth, nextBounds.maxHeight)
      const sized = getGalleryModalSize(naturalWidth, naturalHeight, nextBounds)
      setDisplaySize(fitted)
      setModalSize({ width: sized.modalWidth, height: sized.modalHeight })
    },
    [],
  )

  useEffect(() => {
    if (!current) return
    const b = getGalleryPreviewBounds()
    setDisplaySize({ width: b.maxWidth, height: b.maxHeight })
    setModalSize({ width: b.maxModalWidth, height: b.maxModalHeight })

    if (current.type === MessageTypes.VIDEO) return

    const uri = getMediaPath(current)
    if (!uri) return

    let cancelled = false
    RNImage.getSize(
      uri,
      (w, h) => {
        if (!cancelled) applyFit(w, h)
      },
      () => {
        if (!cancelled) {
          const fallback = getGalleryPreviewBounds()
          setDisplaySize({ width: fallback.maxWidth, height: fallback.maxHeight })
          setModalSize({ width: fallback.maxModalWidth, height: fallback.maxModalHeight })
        }
      },
    )
    return () => {
      cancelled = true
    }
  }, [current, bounds.maxModalWidth, bounds.maxModalHeight, applyFit])

  const previewUri = useMemo(() => {
    if (!current?.message_id) return undefined
    const attach = messageStore.messages.get(current.message_id)?.attach
    return attach?.localVideoPreviewImageUri || attach?.previewUri
  }, [current?.message_id, messageStore.messages])

  if (!current) return null

  const onSaveAsset = async (url: string) => {
    try {
      const httpUrl = staticUrl(url)
      setLoading(true)
      await saveAssetAsync({ uri: httpUrl })
    } catch (e) {
      globalToast.show(e?.message)
    } finally {
      setLoading(false)
    }
  }

  const mediaFrameStyle = {
    width: displaySize.width,
    height: displaySize.height,
  }

  const mediaStyle = {
    width: displaySize.width,
    height: displaySize.height,
    objectFit: "contain" as const,
  }

  const shellStyle = {
    width: modalSize.width,
    height: modalSize.height,
    maxWidth: bounds.maxModalWidth,
    maxHeight: bounds.maxModalHeight,
  }

  return (
    <ModalShell style={shellStyle}>
      <Toolbar>
        <ToolbarGroup>
          <ToolbarButton type="button" aria-label="关闭" onClick={handleClose}>
            <IconFont name="close" size={16} color={iconColor} />
          </ToolbarButton>
        </ToolbarGroup>

        <ToolbarMeta>
          <ToolbarTitle>{isVideo ? "视频预览" : "图片预览"}</ToolbarTitle>
          {hasMultiple ? (
            <ToolbarSubtitle>
              {currentIndex + 1} / {images.length}
            </ToolbarSubtitle>
          ) : null}
        </ToolbarMeta>

        <ToolbarGroupEnd>
          <ToolbarButton
            type="button"
            aria-label="下载"
            disabled={loading}
            onClick={() => onSaveAsset(getMediaPath(current))}
          >
            {loading ? (
              <Loading />
            ) : (
              <IconFont name="xiazai" size={18} color={iconColor} />
            )}
          </ToolbarButton>
        </ToolbarGroupEnd>
      </Toolbar>

      <PreviewArea>
        {hasMultiple ? (
          <>
            <NavPrev
              type="button"
              aria-label="上一张"
              disabled={currentIndex <= 0}
              onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
            >
              <IconFont name="left" size={14} color={iconColor} />
            </NavPrev>
            <NavNext
              type="button"
              aria-label="下一张"
              disabled={currentIndex >= images.length - 1}
              onClick={() => setCurrentIndex((prev) => Math.min(images.length - 1, prev + 1))}
            >
              <IconFont name="right" size={14} color={iconColor} />
            </NavNext>
          </>
        ) : null}

        <MediaFrame style={mediaFrameStyle}>
          {isVideo ? (
            <Video
              previewImageUri={previewUri}
              style={mediaStyle}
              isLooping={false}
              forceStart
              src={getMediaPath(current)}
              onLoad={(size) => {
                if (size?.width && size?.height) applyFit(size.width, size.height)
              }}
            />
          ) : (
            <FastImage
              style={mediaStyle}
              src={{ uri: getMediaPath(current) }}
              resizeMode="contain"
            />
          )}
        </MediaFrame>
      </PreviewArea>
    </ModalShell>
  )
}

export default ImageGallery
